import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Autocomplete } from "@mui/material";
import DeleteModal from "components/shared/DeleteModal";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCompanyList } from "store/adminPanelSlice";
import type { ICompanyGroup } from "store/companyGroups";
import {
    assignCompanyToGroup,
    deleteCompanyGroup,
    fetchCompanyGroups,
    fetchGroupCompanies,
    unassignCompanyFromGroup,
} from "store/companyGroups/company-groups-use-cases";
import { clearSelectedGroup } from "store/companyGroups/companyGroupsStore";
import type { ICompany } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import CompanyGroupFormModal from "./CompanyGroupFormModal";
import SharedFieldsEditor from "./SharedFieldsEditor";

const CompanyGroupsPanel = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { groups, groupsStatus, members, selectedGroupId, membersStatus } = useAppSelector(
        (state) => state.companyGroups,
    );
    const companies = useAppSelector((state) => state.adminPanel.companies.data);

    const [formOpen, setFormOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<ICompanyGroup | null>(null);
    const [groupToDelete, setGroupToDelete] = useState<ICompanyGroup | null>(null);
    const [companyToAdd, setCompanyToAdd] = useState<ICompany | null>(null);

    useEffect(() => {
        dispatch(fetchCompanyGroups());
        dispatch(getCompanyList());
    }, [dispatch]);

    const selectedGroup = groups.find((g) => g.id === selectedGroupId) ?? null;

    const openCreate = () => {
        setEditingGroup(null);
        setFormOpen(true);
    };

    const openEdit = (group: ICompanyGroup) => {
        setEditingGroup(group);
        setFormOpen(true);
    };

    const selectGroup = (group: ICompanyGroup) => {
        dispatch(fetchGroupCompanies(group.id));
    };

    const addCompany = async () => {
        if (!companyToAdd || !selectedGroupId) return;
        const result = await (dispatch(
            assignCompanyToGroup({ groupId: selectedGroupId, companyId: companyToAdd.id }),
        ) as any);
        setCompanyToAdd(null);
        if (!result.error) dispatch(fetchGroupCompanies(selectedGroupId));
    };

    const removeCompany = async (companyId: number) => {
        if (!selectedGroupId) return;
        await (dispatch(unassignCompanyFromGroup({ groupId: selectedGroupId, companyId })) as any);
        dispatch(fetchGroupCompanies(selectedGroupId));
    };

    const memberIds = new Set(members.map((m) => m.id));
    const assignableCompanies = companies.filter((c) => !memberIds.has(c.id));

    return (
        <div className="flex flex-col gap-6">
            <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">{t("CompanyGroups.Description")}</p>
                <StyledButton buttonType="primary" onClick={openCreate}>
                    {t("CompanyGroups.CreateGroup")}
                </StyledButton>
            </div>

            <div className="flex gap-6">
                {/* Groups list */}
                <div className="flex flex-col gap-2 w-1/2">
                    {groupsStatus === "pending" && <p>{t("CompanyGroups.Loading")}</p>}
                    {groupsStatus !== "pending" && groups.length === 0 && (
                        <p className="text-gray-500">{t("CompanyGroups.NoGroups")}</p>
                    )}
                    {groups.map((group) => (
                        <div
                            key={group.id}
                            className={`flex items-center justify-between border rounded-md p-3 cursor-pointer ${
                                group.id === selectedGroupId ? "border-black" : "border-gray-200"
                            }`}
                            onClick={() => selectGroup(group)}
                        >
                            <span className="font-medium">{group.name}</span>
                            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                <StyledButton
                                    buttonType="secondary"
                                    size="small"
                                    onClick={() => openEdit(group)}
                                    tooltipText={t("CompanyGroups.Edit")}
                                >
                                    <EditIcon fontSize="small" />
                                </StyledButton>
                                <StyledButton
                                    buttonType="danger"
                                    size="small"
                                    onClick={() => setGroupToDelete(group)}
                                    tooltipText={t("CompanyGroups.Delete")}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </StyledButton>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Selected group members */}
                <div className="flex flex-col gap-3 w-1/2">
                    {membersStatus === "pending" && <p>{t("CompanyGroups.Loading")}</p>}

                    {membersStatus !== "pending" && selectedGroup ? (
                        <>
                            <SharedFieldsEditor groupId={selectedGroup.id} />

                            <h3 className="font-semibold">
                                {t("CompanyGroups.Members")} — {selectedGroup.name}
                            </h3>
                            <div className="flex gap-2 items-start">
                                <Autocomplete
                                    className="flex-1"
                                    size="small"
                                    options={assignableCompanies}
                                    value={companyToAdd}
                                    onChange={(_e, value) => setCompanyToAdd(value)}
                                    getOptionLabel={(c) => `${c.name} (#${c.id})`}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                    renderInput={(params) => (
                                        <StyledTextField {...params} placeholder={t("CompanyGroups.AddCompany")} />
                                    )}
                                />
                                <StyledButton buttonType="primary" disabled={!companyToAdd} onClick={addCompany}>
                                    {t("CompanyGroups.Add")}
                                </StyledButton>
                            </div>
                            {members.length === 0 ? (
                                <p className="text-gray-500">{t("CompanyGroups.NoMembers")}</p>
                            ) : (
                                <ul className="flex flex-col gap-1">
                                    {members.map((member) => (
                                        <li
                                            key={member.id}
                                            className="flex items-center justify-between border border-gray-200 rounded-md p-2"
                                        >
                                            <span>{`${member.name} #${member.id}`}</span>
                                            <StyledButton
                                                buttonType="danger"
                                                size="small"
                                                onClick={() => removeCompany(member.id)}
                                            >
                                                {t("CompanyGroups.Remove")}
                                            </StyledButton>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    ) : (
                        <p className="text-gray-500">{t("CompanyGroups.SelectGroupHint")}</p>
                    )}
                </div>
            </div>

            <CompanyGroupFormModal open={formOpen} onClose={() => setFormOpen(false)} group={editingGroup} />

            <DeleteModal
                open={!!groupToDelete}
                onClose={() => setGroupToDelete(null)}
                onDelete={async () => {
                    if (groupToDelete) {
                        await (dispatch(deleteCompanyGroup(groupToDelete.id)) as any);
                        if (selectedGroupId === groupToDelete.id) dispatch(clearSelectedGroup());
                    }
                }}
                elementTitle={groupToDelete?.name ?? ""}
                needsDoubleQuotes
                warningMessage={t("CompanyGroups.DeleteWarning")}
            />
        </div>
    );
};

export default CompanyGroupsPanel;
